// Sugestão inteligente de reagendamento (Motoboy v3, item 4 do pedido do Bruno):
// analisa a agenda futura do operador, a carga de visitas por dia e a proximidade
// geográfica entre os leads pendentes e as rotas já agendadas, e sugere a melhor data.
import { haversineKm } from './routeOptimizer';

export interface PontoSugestao {
    lat?: number | null;
    lng?: number | null;
    cidade?: string | null;
}

export interface DiaAgendaFutura {
    data: string; // YYYY-MM-DD
    leads: PontoSugestao[];
}

export interface SugestaoData {
    data: string;
    texto: string; // justificativa curta mostrada ao operador
}

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const DIA_LONGO = (iso: string) =>
    new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' });

const centroide = (pts: PontoSugestao[]): { lat: number; lng: number } | null => {
    const validos = pts.filter(p => p.lat != null && p.lng != null);
    if (validos.length === 0) return null;
    return {
        lat: validos.reduce((s, p) => s + Number(p.lat), 0) / validos.length,
        lng: validos.reduce((s, p) => s + Number(p.lng), 0) / validos.length,
    };
};

/**
 * Pontua os próximos 7 dias e devolve a melhor data (ou null se nada se destacar).
 * Critérios: rota futura passando na mesma região (peso maior), mesma cidade,
 * dias com agenda mais livre; domingos são evitados.
 */
export const sugerirDataReagendamento = (
    pendentes: PontoSugestao[],
    agendaFutura: DiaAgendaFutura[],
): SugestaoData | null => {
    if (pendentes.length === 0) return null;

    const hoje = new Date();
    const cPend = centroide(pendentes);
    const cidadesPend = new Set(pendentes.map(p => (p.cidade || '').trim().toLowerCase()).filter(Boolean));
    const porData = new Map(agendaFutura.map(d => [d.data, d.leads]));
    const cargas = agendaFutura.map(d => d.leads.length);
    const cargaMedia = cargas.length > 0 ? cargas.reduce((s, n) => s + n, 0) / cargas.length : 0;

    let melhor: { data: string; score: number; motivo: string } | null = null;

    for (let i = 1; i <= 7; i++) {
        const d = new Date(hoje);
        d.setDate(d.getDate() + i);
        if (d.getDay() === 0) continue; // domingo
        const iso = isoDate(d);
        const leadsDia = porData.get(iso) || [];

        let score = 0;
        let motivo = '';

        // Proximidade geográfica com a rota já agendada no dia
        const cDia = centroide(leadsDia);
        if (cPend && cDia) {
            const dist = haversineKm(cPend, cDia);
            if (dist <= 3) { score += 6; motivo = 'sua rota já passa pela mesma região'; }
            else if (dist <= 7) { score += 3; motivo = 'sua rota passa perto da região destes leads'; }
        }

        // Mesma cidade
        if (!motivo && leadsDia.length > 0 && cidadesPend.size > 0) {
            const mesmaCidade = leadsDia.some(l => cidadesPend.has((l.cidade || '').trim().toLowerCase()));
            if (mesmaCidade) { score += 2; motivo = 'você já tem visitas na mesma cidade'; }
        }

        // Carga do dia: agenda mais livre pontua; dia lotado desconta
        const carga = leadsDia.length;
        if (carga === 0) { score += 2; if (!motivo) motivo = 'sua agenda está livre neste dia'; }
        else if (carga < cargaMedia) { score += 1; if (!motivo) motivo = 'é o dia com a agenda mais leve'; }
        else if (carga > Math.max(cargaMedia * 1.5, 15)) score -= 2;

        // Empate: prefere o dia mais próximo (score já calculado; desempate por i menor)
        if (!melhor || score > melhor.score) melhor = { data: iso, score, motivo: motivo || 'agenda disponível' };
    }

    if (!melhor || melhor.score <= 0) return null;
    return {
        data: melhor.data,
        texto: `Sugerimos ${DIA_LONGO(melhor.data)} — ${melhor.motivo}.`,
    };
};
